import { Switch, Route, Redirect } from 'react-router-dom';

import { CycleEvaluationFormNavigator } from './CycleEvaluation/CycleEvaluationFormNavigator';
import { CycleEvaluationGroupFormNavigator } from './CycleEvaluation/CycleEvaluationGroupFormNavigator';
import { Evaluation360FormNavigator } from './Evaluation360/Evaluation360FormNavigator';
import { WithPermission } from './PermissionGuard';
import { ProFeatureRoute } from './ProFeatureRoute';
import { SelfAssessmentFormNavigator } from './SelfAssessment/SelfAssessmentFormNavigator';
import { RouteName } from './types';
import { WithEmployeePortalAccess } from './WithEmployeePortalAccess';

import type { UserPermissions } from '@/types/directory/User';

import { FeatureDisabledState } from '@/components/FeatureDisabledState/FeatureDisabledState';
import { FeatureGate } from '@/components/FeatureGate/FeatureGate';
import { NotFoundState } from '@/components/NotFoundState/NotFoundState';
import { SurveyRouteWrapper } from '@/components/SurveyRouteWrapper/SurveyRouteWrapper';
import VisionValuesPage from '@/components/VisionValues/VisionValuesPage';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { FeatureFlag } from '@/env';
import { CompetenciesPage } from '@/pages/Competencies/CompetenciesPage';
import { CompetencyProfileWizardPage } from '@/pages/Competencies/CompetencyProfileWizardPage';
import { CompetencyProfilesAdminPage } from '@/pages/CompetencyProfilesAdmin/CompetencyProfilesAdminPage';
import { DashboardPage as CycleEvaluationDashboardPage } from '@/pages/CycleEvaluation/Dashboard/DashboardPage';
import { LaunchSummaryPage } from '@/pages/CycleEvaluation/LaunchSummary/LaunchSummaryPage';
import { TranslateQuestionnairePage as CycleEvalTranslateQuestionnairePage } from '@/pages/CycleEvaluation/TranslateQuestionnaire/TranslateQuestionnairePage';
import { EmployeePortalPage } from '@/pages/EmployeePortal/EmployeePortalPage';
import { DashboardPage } from '@/pages/Evaluation360/Dashboard/DashboardPage';
import { TranslateQuestionnairePage as Eval360TranslateQuestionnairePage } from '@/pages/Evaluation360/TranslateQuestionnaire/TranslateQuestionnairePage';
import { ManagerAssessmentEvaluationPage } from '@/pages/ManagerAssessment/Evaluation/ManagerAssessmentEvaluationPage';
import { ManagerAssessmentMeetPage } from '@/pages/ManagerAssessment/Meet/ManagerAssessmentMeetPage';
import { ManagerAssessmentPrepareMeetingPage } from '@/pages/ManagerAssessment/PrepareMeeting/ManagerAssessmentPrepareMeetingPage';
import ManagerAssessmentResultsPage from '@/pages/ManagerAssessment/Results/ManagerAssessmentResultsPage';
import { ManagerAssessmentSetupPage } from '@/pages/ManagerAssessment/Setup/ManagerAssessmentSetupPage';
import { ManageTemplatesPage } from '@/pages/ManageTemplates/ManageTemplatesPage';
import { MeetingPlanTemplateEditorPage } from '@/pages/ManageTemplates/MeetingPlanTemplate/MeetingPlanTemplateEditorPage';
import { TemplateQuestionnaireCreationPage } from '@/pages/ManageTemplates/TemplateQuestionnaire/TemplateQuestionnaireCreationPage';
import { TranslateTemplatePage } from '@/pages/ManageTemplates/TranslateTemplate/TranslateTemplatePage';
import CreateObjectiveTypePage from '@/pages/Objectives/CreateObjectiveTypePage';
import EmployeesWithoutObjectivesPage from '@/pages/Objectives/EmployeesWithoutObjectives/EmployeesWithoutObjectivesPage';
import ObjectiveDetailPage from '@/pages/Objectives/ObjectiveDetailPage';
import ObjectiveFormPage from '@/pages/Objectives/ObjectiveFormPage';
import ObjectivesPage from '@/pages/Objectives/ObjectivesPage';
import PillarObjectivesPage from '@/pages/Objectives/PillarObjectivesPage';
import { PerformanceProfile } from '@/pages/PerformanceProfile';
import { CalibrationReportPage } from '@/pages/Reports/Calibration/CalibrationReportPage';
import { CompensationReportPage } from '@/pages/Reports/Compensation/CompensationReportPage';
import { CompetencyMatrixReportPage } from '@/pages/Reports/CompetencyMatrix/CompetencyMatrixReportPage';
import ResultsPage from '@/pages/Results/ResultsPage';
import { SuccessMessagePage } from '@/pages/shared/SuccessMessage/SuccessMessagePage';
import { CommentThreadPage } from '@/pages/SurveyAnswer/CommentThread/CommentThreadPage';
import { SurveyAnswerPage } from '@/pages/SurveyAnswer/SurveyAnswer/SurveyAnswerPage';
import { ThematicMeetingPlanPage } from '@/pages/ThematicMeetings/Plan/ThematicMeetingPlanPage';
import { ThematicMeetingsPage } from '@/pages/ThematicMeetings/ThematicMeetingsPage';

// The host hands this module every address under its prefixes, so its own `/error/404` never sees
// an address we do not serve: without this last route the module would render an empty page, which
// reads as a broken screen rather than as a dead link.
const ROUTE_NOT_FOUND = (
    <NotFoundState
        titleKey={'errors.pageNotFoundTitle'}
        messageKey={'errors.pageNotFoundMessage'}
    />
);

const REPORTS_DISABLED = (
    <FeatureDisabledState
        flag={FeatureFlag.REPORTS}
        titleKey={'reports.disabled.title'}
        messageKey={'reports.disabled.message'}
    />
);

// Both thematic-meeting routes share one permission scope; a module-level constant keeps them in
// step and avoids handing `WithPermission` a brand-new array on every render.
const THEMATIC_MEETINGS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

// Mirrors the backend's `ReportAccessScope.EnsureCanViewReports`: either performance-management
// scope is enough to view a report.
const REPORTS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

// The evaluation-cycles dashboard has two audiences: the cycle administrators, and the objectives
// managers who come for its "Objective periods" tab. The dashboard itself shows each reader only
// the tabs their scope covers.
const CYCLE_DASHBOARD_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllDepartmentsPerformance',
    'canManageAllEmployeesObjectives',
];

// Mirrors the backend's `CanAccessObjectives`, which gates writing objectives and the pillar summaries:
// the whole company, the caller's team, or their own objectives. Reading needs none of them --
// every employee reads the objectives they hold -- so the list and detail pages sit behind the flag
// alone, and the server narrows what each reader sees. The pillar page stands on its pillar's totals.
const OBJECTIVES_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllEmployeesObjectives',
    'canManageTeamObjectives',
    'canManageTeamPerformance',
    'canEditOwnObjectives',
];

// Mirrors the backend's `CanManageOthersObjectives`: the manager-facing tools of the module.
const OBJECTIVE_MANAGERS_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageAllEmployeesObjectives',
    'canManageTeamObjectives',
    'canManageTeamPerformance',
];

// The calibration backend checks the same two performance-management scopes: a team manager sees
// their own team's calibration, an all-departments manager sees every team's.
const CALIBRATION_PERMISSIONS: (keyof UserPermissions)[] = [
    'canManageTeamPerformance',
    'canManageAllDepartmentsPerformance',
];

export const RootNavigator = () => {
    return (
        <Switch>
            <Redirect
                exact
                from={'/'}
                to={RouteName.Evaluation360}
            />

            <Route
                exact
                path={RouteName.CompetencyProfileCreate}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Competencies}>
                        <WithPermission permissionKey={'canManageCompetencies'}>
                            <CompetencyProfileWizardPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                path={RouteName.CompetencyProfilesAdmin}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.CompetencyProfiles}>
                        <WithPermission permissionKey={'canAssignCompetencyProfiles'}>
                            <CompetencyProfilesAdminPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                path={RouteName.Competencies}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Competencies}>
                        <WithPermission permissionKey={'canManageCompetencies'}>
                            <CompetenciesPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.VisionValues}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.VisionValues}>
                        <WithPermission permissionKey={'canManageCompanyValues'}>
                            <VisionValuesPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            {/* The portal opens to the company administrator, to the role permission every role holds by
                default, or to a performance scope (#17107); each tab asks the server for the reader's own
                data only. Without access the reader goes to the default route, as for any refused page. */}
            <Route
                exact
                path={RouteName.EmployeePortal}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.EmployeePortal}>
                        <WithEmployeePortalAccess>
                            <EmployeePortalPage />
                        </WithEmployeePortalAccess>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.Objectives}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <ObjectivesPage />
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectivePillar}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                            <PillarObjectivesPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectivesNew}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                            <CreateObjectiveTypePage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectivesNewType}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                            <ObjectiveFormPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectiveEdit}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <WithPermission anyOfPermissionKeys={OBJECTIVES_PERMISSIONS}>
                            <ObjectiveFormPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            {/* Before `ObjectiveDetail`: `/objectives/:id` would otherwise swallow this path. */}
            <Route
                exact
                path={RouteName.ObjectivesWithoutObjective}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <WithPermission anyOfPermissionKeys={OBJECTIVE_MANAGERS_PERMISSIONS}>
                            <EmployeesWithoutObjectivesPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ObjectiveDetail}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Objectives}>
                        <ObjectiveDetailPage />
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.CycleEvaluation}
                render={() => (
                    <WithPermission anyOfPermissionKeys={CYCLE_DASHBOARD_PERMISSIONS}>
                        <CycleEvaluationDashboardPage />
                    </WithPermission>
                )}
            />

            <Route
                path={RouteName.CycleEvaluationQuestionnaireTranslation}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvalTranslateQuestionnairePage />
                    </WithPermission>
                )}
            />

            <Route
                path={`${RouteName.CycleEvaluationGroupForm}/:step/:cycleEvaluationGroupId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvaluationGroupFormNavigator />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={`${RouteName.CycleEvaluationGroupLaunchSummary}/:cycleEvaluationGroupId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <LaunchSummaryPage />
                    </WithPermission>
                )}
            />

            <Route
                path={`${RouteName.CycleEvaluationForm}/:step/:cycleEvaluationId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <CycleEvaluationFormNavigator />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.Evaluation360}
                component={DashboardPage}
            />

            <Route
                path={RouteName.SelfAssessmentQuestionnaireTranslation}
                component={Eval360TranslateQuestionnairePage}
            />

            <Route
                path={RouteName.Eval360QuestionnaireTranslation}
                component={Eval360TranslateQuestionnairePage}
            />

            <Route
                path={`${RouteName.Eval360Form}/:step/:cycleInstanceId`}
                component={Evaluation360FormNavigator}
            />

            <Route
                path={`${RouteName.SelfAssessmentForm}/:step/:cycleInstanceId`}
                component={SelfAssessmentFormNavigator}
            />

            <Route
                path={`${RouteName.SurveyAnswer}/:reviewAnswerId`}
                render={() => (
                    <SurveyRouteWrapper>
                        <SurveyAnswerPage />
                    </SurveyRouteWrapper>
                )}
            />

            <Route
                path={`${RouteName.SurveyAnswer}`}
                render={() => (
                    <SurveyRouteWrapper>
                        <SurveyAnswerPage />
                    </SurveyRouteWrapper>
                )}
            />

            <Route
                path={`${RouteName.CommentThread}/:threadKey/:linkedThreadId?`}
                render={() => (
                    <SurveyRouteWrapper>
                        <CommentThreadPage />
                    </SurveyRouteWrapper>
                )}
            />

            <Route
                path={`${RouteName.TemplateTranslation}/:templateId`}
                component={TranslateTemplatePage}
            />

            <Route
                path={`${RouteName.TemplateQuestionnaire}/:templateId`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <TemplateQuestionnaireCreationPage />
                    </WithPermission>
                )}
            />

            {/* Same capability as the Templates page it is reached from, and the same the write
                route asks for: a manager of one team may read the company's plans, never reword them.
                Meeting plans belong to Meetings, a Performance PRO module. */}
            <Route
                path={`${RouteName.MeetingPlanTemplate}/:templateId`}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Meetings}>
                        <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                            <MeetingPlanTemplateEditorPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            {/* Administering templates is an all-departments capability: the page reads
                Amelio's app-wide library, which the API serves to that scope only. A team
                manager reaching it fired one refused request per evaluation type (#14600). */}
            <Route
                path={`${RouteName.ManageTemplates}`}
                render={() => (
                    <WithPermission permissionKey={'canManageAllDepartmentsPerformance'}>
                        <ManageTemplatesPage />
                    </WithPermission>
                )}
            />

            <Route
                exact
                path={RouteName.ManagerAssessment}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.ManagerAssessment}>
                        <ManagerAssessmentSetupPage />
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentEvaluation}/:cycleInstanceId`}
                component={ManagerAssessmentEvaluationPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentResults}/:cycleInstanceId`}
                component={ManagerAssessmentResultsPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentPrepareMeeting}/:cycleInstanceId`}
                component={ManagerAssessmentPrepareMeetingPage}
            />

            <Route
                exact
                path={`${RouteName.ManagerAssessmentMeet}/:cycleInstanceId`}
                component={ManagerAssessmentMeetPage}
            />

            <Route
                path={RouteName.SuccessMessage}
                component={SuccessMessagePage}
            />

            <Route
                path={`${RouteName.Eval360Results}/:reviewInstanceId/:linkedThreadId?`}
                component={ResultsPage}
            />

            <Route
                path={`${RouteName.PeriodResults}/:employeeId/:reviewType`}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.ManagerAssessment}>
                        <ResultsPage />
                    </ProFeatureRoute>
                )}
            />

            <Route
                path={RouteName.PerformanceProfile}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.PerformanceProfile}>
                        <PerformanceProfile />
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.ThematicMeetings}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Meetings}>
                        <WithPermission anyOfPermissionKeys={THEMATIC_MEETINGS_PERMISSIONS}>
                            <ThematicMeetingsPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={`${RouteName.ThematicMeetings}/:meetingId`}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Meetings}>
                        <WithPermission anyOfPermissionKeys={THEMATIC_MEETINGS_PERMISSIONS}>
                            <ThematicMeetingPlanPage />
                        </WithPermission>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.CompetencyMatrixReport}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Reports}>
                        <FeatureGate
                            flag={FeatureFlag.REPORTS}
                            fallback={REPORTS_DISABLED}
                        >
                            <WithPermission anyOfPermissionKeys={REPORTS_PERMISSIONS}>
                                <CompetencyMatrixReportPage />
                            </WithPermission>
                        </FeatureGate>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.CalibrationReport}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Reports}>
                        <FeatureGate
                            flag={FeatureFlag.CALIBRATION_REPORT}
                            fallback={
                                <FeatureDisabledState
                                    flag={FeatureFlag.CALIBRATION_REPORT}
                                    titleKey={'calibration.disabled.title'}
                                    messageKey={'calibration.disabled.message'}
                                />
                            }
                        >
                            <WithPermission anyOfPermissionKeys={CALIBRATION_PERMISSIONS}>
                                <CalibrationReportPage />
                            </WithPermission>
                        </FeatureGate>
                    </ProFeatureRoute>
                )}
            />

            <Route
                exact
                path={RouteName.CompensationReport}
                render={() => (
                    <ProFeatureRoute feature={PerformanceProFeature.Reports}>
                        <FeatureGate
                            flag={FeatureFlag.REPORTS}
                            fallback={REPORTS_DISABLED}
                        >
                            <FeatureGate
                                flag={FeatureFlag.COMPENSATION_PLANNING}
                                fallback={
                                    <FeatureDisabledState
                                        flag={FeatureFlag.COMPENSATION_PLANNING}
                                        titleKey={'compensation.disabled.title'}
                                        messageKey={'compensation.disabled.message'}
                                    />
                                }
                            >
                                <WithPermission anyOfPermissionKeys={REPORTS_PERMISSIONS}>
                                    <CompensationReportPage />
                                </WithPermission>
                            </FeatureGate>
                        </FeatureGate>
                    </ProFeatureRoute>
                )}
            />

            {/* Last on purpose: a pathless route matches whatever none of the above did. */}
            <Route render={() => ROUTE_NOT_FOUND} />
        </Switch>
    );
};
